/**
 * Responsibility: Orchestrate admin operations between UI and stores.
 * Scope: High-level admin actions; persistence stays in stores.
 */
import { adminStore } from '../ui/stores/admin-store'
import { commerceStore } from '../ui/stores/commerce-store'
import { emailService } from './email-service'
import { sysNotify } from '../app-states'

export const adminService = {
  async sendInvite(email: string): Promise<{ ok: boolean; message: string }> {
    if (!email || !email.includes('@')) {
      return { ok: false, message: 'A valid email is required.' }
    }
    try {
      const result = await emailService.sendAdminInvite(email)
      if (result.ok) {
        sysNotify(`Invite sent to ${email}`)
      } else {
        sysNotify(`Invite failed: ${result.message}`)
      }
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      sysNotify(`Invite failed: ${message}`)
      return { ok: false, message }
    }
  },

  async saveCommercePlan(plan: any): Promise<void> {
    try {
      await adminStore.saveCommercePlan(plan)
      sysNotify(`Saved plan ${plan.id}.`)
    } catch (error) {
      const message = `Could not save plan: ${String(error).slice(0, 140)}`
      sysNotify(message)
      throw error
    }
  },

  async saveRoleGrant(grant: any): Promise<void> {
    try {
      await adminStore.saveRoleGrant(grant)
      sysNotify(`Role saved for ${grant.email || grant.uid || 'user'}.`)
    } catch (error) {
      const message = `Could not save role: ${String(error).slice(0, 140)}`
      sysNotify(message)
      throw error
    }
  },

  async saveFlags(flags: any): Promise<void> {
    try {
      await adminStore.saveFlags(flags)
      sysNotify('Admin flags saved.')
    } catch (error) {
      const message = `Could not save admin flags: ${String(error).slice(0, 140)}`
      sysNotify(message)
      throw error
    }
  },

  async quickSetRole(grant: any, role: string): Promise<void> {
    try {
      await adminStore.saveRoleGrant({ ...grant, role })
      sysNotify(`Role ${role} granted to ${grant.email || grant.uid}.`)
    } catch (error) {
      sysNotify(`Could not update role: ${String(error).slice(0, 140)}`)
    }
  },

  async saveTesterGrant(grant: any): Promise<void> {
    try {
      await adminStore.saveTesterGrant(grant)
      sysNotify(`Tester grant saved for ${grant.email || grant.uid}.`)
    } catch (error) {
      sysNotify(`Could not save tester grant: ${String(error).slice(0, 140)}`)
      throw error
    }
  },

  async revokeTesterGrant(grant: any): Promise<void> {
    try {
      await adminStore.deleteTesterGrant(grant)
      sysNotify(`Tester grant revoked for ${grant.email || grant.uid}.`)
    } catch (error) {
      sysNotify(`Could not revoke tester grant: ${String(error).slice(0, 140)}`)
    }
  }
}
